import React, { type KeyboardEvent, Dispatch, useEffect, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import type { DebouncedFunc } from 'lodash'
import classNames from 'classnames'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
// Hooks
import { useTranslations, useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'

// Components
import { getMainCategoryIcon } from '@dustin-web/microsite-components/src/utils/main-category-icons'
import { stripTrailingSlash } from '@dustin-web/microsite-components/src/utils/urls'
import Accordion from '@dustin-web/microsite-components/src/accordion'
import {
  TextSkeleton,
  ArticleCardMiniSkeleton,
} from '@dustin-web/microsite-components/src/skeleton'
import { ArticleCardListMini } from '@dustin-web/content-components/src/article-card-list'
import Icon from '@dustin-web/microsite-components/src/icon'

// Types
import type {
  GetCategoryNavigationQuery,
  GetCategoryNavigationShallowQuery,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import {
  DesktopState,
  DesktopStateAction,
  DesktopStateActionType,
  useHeaderContext,
} from './header-context'
import {
  NavigationEventType,
  NavigationSelectionType,
} from '@dustin-web/microsite-components/src/utils/digital-data-types'

// GraphQl
import {
  GET_CATEGORY_NAVIGATION,
  GET_CATEGORY_NAVIGATION_SHALLOW,
} from '@dustin-web/microsite-data/src/graphql/product/category-navigation.graphql'

// HOCs
import { withArticles } from '@dustin-web/microsite-data/src/hocs'

// Types Local
type Props = {
  showMobileCategories?: boolean
}
type CommonProps = {
  allCategories: any
  showCampaigns?: boolean
  translate: (param: string, obj?: any) => string
}
type DesktopProps = {
  mainCategories: any
  setCurrentId: DebouncedFunc<any>
  loading: boolean
  data: any
  showSkeleton: boolean
  hasArticlesEntrances: boolean
  articleIds: Array<number> | number
  desktop: DesktopState
  focusOnSubCategories: boolean
  isCurrentId: (id: string) => boolean
  setFocusOnSubCategories: (state: boolean) => void
  dispatchDesktop: Dispatch<DesktopStateAction>
}
type CommonLinkProps = {
  icon: string
  gaTitle: string | undefined
  text: string
}
type CategoryListButtonProps = {
  onClick: () => void
  dataTestIdOverride?: string
}
type CategoryListLink = {
  onActive: () => void
  setFocusOnSubCategories?: (state: boolean) => void
  linkHref: string
  isCurrent: boolean
  onClick: () => void
  subCategories?: boolean
}
type CategorySubCategoryColumns = {
  data: any
  categoryItem: any
  isCurrentId: (arg: string) => boolean
  narrow: boolean
  focusOnSubCategories?: boolean
}

// Utils
const TOP_CATEGORY_ID = '2037738'
const BARGAIN_CATEGORY_ID = '2029898'
const WITH_ARTICLES_CATEGORY_ID = '2037759'
const articleIdsMaeketsObj = {
  DustinSweden: [139476, 266875],
  DustinHomeSweden: [225880],
  DustinNorway: [139551, 267378],
  DustinHomeNorway: [226555],
  DustinDenmark: [139493],
  DustinHomeDenmark: [226545],
  DustinFinland: [139552],
  DustinHomeFinland: [226556],
  DustinNetherlands: [185174, 236220],
  DustinHomeNetherlands: [],
  DustinBelgium: [], // TODO: Add article IDs for Belgium?
}

const generateUnicCatId = (allExistingIDs: Array<string>, length: number): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let unicCatID = ''

  do {
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length)
      unicCatID += characters[randomIndex]
    }
  } while (allExistingIDs.includes(unicCatID))

  return unicCatID
}
const getCategoryType = (categoryId: string) =>
  categoryId.includes('StructureGroup_')
    ? NavigationSelectionType.SoftCategory
    : NavigationSelectionType.Category
export const getGaTitle = (id: string) => {
  return {
    '2037749': 'computer-and-tablets',
    '2037751': 'computer-accessories',
    '2029798': 'server-and-storage',
    '2029786': 'network',
    '2029772': 'computer-components',
    '2029808': 'client-storage',
    '2037759': 'printers-and-supplies',
    '2029810': 'phones-and-gps',
    '2037866': 'audio-and-video',
    '2029978': 'point-of-sale',
    '2029788': 'office-equipment',
    '2037737': 'software',
    '2029898': 'bargain',
  }[id]
}

// Components
const ArticlesListWithData = withArticles(props => {
  const { translate } = useTranslations()
  const onCardClick = (url: string): void => {
    DigitalData.pushNavigationInteraction(NavigationEventType.MainMenu, url, 'link_click')
  }
  if (!props.articlesList.length) {
    return translate('No_Articles') // needs to refactor and not display entire section if no articles are found
  }
  if (props.isLoading) {
    return (
      <>
        <ArticleCardMiniSkeleton className="u-mb-24" shade="light-on-dark-bg" />
        <ArticleCardMiniSkeleton className="u-mb-24" shade="light-on-dark-bg" />
        <TextSkeleton type="h4" shade="light-on-dark-bg" />
      </>
    )
  }
  return <ArticleCardListMini onCardClick={onCardClick} {...props} />
})

const CategoryTreeMenu = ({ showMobileCategories = false }: Props) => {
  const { dispatchDesktop, desktop } = useHeaderContext()
  const { translate } = useTranslations()
  const { site } = useSiteContext()
  const [focusOnSubCategories, setFocusOnSubCategories] = useState(false)
  const isActive = desktop.activeMegaMenu === 'category' || showMobileCategories
  const [currentId, setCurrentStateId] = useState('')
  const articleIds = articleIdsMaeketsObj[site]
  const hasArticlesEntrances = currentId === WITH_ARTICLES_CATEGORY_ID && !!articleIds.length

  // Load top categories server side for SEO purposes but avoid fetching the full category tree unless category menu is active
  const { data: shallowData } = useGraphql<GetCategoryNavigationShallowQuery>(
    GET_CATEGORY_NAVIGATION_SHALLOW,
    {}
  )
  const { data, isValidating: loading } = useGraphql<GetCategoryNavigationQuery>(
    GET_CATEGORY_NAVIGATION,
    {
      context: {
        skip: !isActive,
      },
    }
  )

  const mainCategories = shallowData?.categoryNavigation?.children || []
  const allCategories = data?.categoryNavigation?.children || []
  const rootCategoryId = mainCategories[0]?.categoryId

  // debounce to improve interaction when moving between menu options
  const setCurrentId = debounce(id => setCurrentStateId(id), 100)
  const isCurrentId = (id: string) => id === currentId

  useEffect(() => {
    if (!loading && rootCategoryId) {
      setCurrentStateId(rootCategoryId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  return (
    <Desktop
      showCampaigns
      mainCategories={mainCategories}
      allCategories={allCategories}
      setCurrentId={setCurrentId}
      loading={loading}
      showSkeleton={loading || !allCategories?.length}
      data={data}
      hasArticlesEntrances={hasArticlesEntrances}
      articleIds={articleIds}
      desktop={desktop}
      focusOnSubCategories={focusOnSubCategories}
      isCurrentId={isCurrentId}
      translate={translate}
      setFocusOnSubCategories={setFocusOnSubCategories}
      dispatchDesktop={dispatchDesktop}
    />
  )
}

const Desktop = ({
  mainCategories,
  allCategories,
  showCampaigns,
  setCurrentId,
  data,
  showSkeleton,
  hasArticlesEntrances,
  articleIds,
  focusOnSubCategories,
  desktop,
  dispatchDesktop,
  isCurrentId,
  translate,
  setFocusOnSubCategories,
}: CommonProps & DesktopProps) => {
  const categoryListRef = useRef<HTMLDivElement>(null)

  const mainExistingIDs = mainCategories.map((cat: any) => cat.categoryId)
  const newsCatID: string = generateUnicCatId(
    [...mainExistingIDs, TOP_CATEGORY_ID, BARGAIN_CATEGORY_ID],
    7
  )
  const campaignsCatID: string = generateUnicCatId(
    [...mainExistingIDs, TOP_CATEGORY_ID, BARGAIN_CATEGORY_ID],
    7
  )

  useEffect(() => {
    if (desktop.activeMegaMenu === desktop.focusMegaMenu) {
      categoryListRef.current?.focus()

      dispatchDesktop({
        type: DesktopStateActionType.SET_FOCUS_MEGA_MENU,
        payload: {
          focusMegaMenu: '',
        },
      })
    }
  }, [desktop.activeMegaMenu, desktop.focusMegaMenu])

  return (
    <div className="row no-gutters u-h-100 a-fade-in">
      <div
        tabIndex={0}
        ref={categoryListRef}
        className="c-header__category-list-wrapper col-3 u-pt-24">
        <ul
          className="c-header__category-list ul-stripped"
          onMouseLeave={() => setCurrentId.cancel()}
          role="tablist">
          <CategoryListLink
            key="bargains"
            onActive={() => setCurrentId(BARGAIN_CATEGORY_ID)}
            linkHref={stripTrailingSlash(translate('Bargains_URL'))}
            gaTitle={getGaTitle(BARGAIN_CATEGORY_ID)}
            icon="bargain"
            text={translate('Show_Bargains')}
            isCurrent={isCurrentId(BARGAIN_CATEGORY_ID)}
            subCategories={false}
            onClick={() => {
              DigitalData.pushNavigationInteraction(
                NavigationEventType.MainMenu,
                'bargains',
                'link_click'
              )
            }}
          />

          {showCampaigns && (
            <CategoryListLink
              key="show_campaigns"
              gaTitle="campaigns"
              icon="label"
              onActive={() => setCurrentId(campaignsCatID)}
              linkHref={stripTrailingSlash(translate('Campaigns_URL'))}
              text={translate('Show_Campaigns')}
              isCurrent={false}
              subCategories={false}
              onClick={() => {
                DigitalData.pushNavigationInteraction(
                  NavigationEventType.MainMenu,
                  'campaigns',
                  'link_click'
                )
              }}
            />
          )}

          <CategoryListLink
            key="news"
            gaTitle="news"
            icon="idea"
            onActive={() => setCurrentId(newsCatID)}
            linkHref={translate('New_Products_URL')}
            text={translate('Header_News')}
            subCategories={false}
            isCurrent={false}
            onClick={() => {
              DigitalData.pushNavigationInteraction(
                NavigationEventType.MainMenu,
                'news',
                'link_click'
              )
            }}
          />

          <li className="c-mobile-header-menu__primary-list-devider-desk"></li>

          {mainCategories.map((child: any, key: number) => (
            <React.Fragment key={child.categoryId + key}>
              <CategoryListLink
                onActive={() => setCurrentId(child.categoryId)}
                linkHref={stripTrailingSlash(child.url || '')}
                gaTitle={getGaTitle(child.categoryId)}
                icon={child.categoryId}
                text={child.categoryDisplayName}
                isCurrent={isCurrentId(child.categoryId)}
                setFocusOnSubCategories={setFocusOnSubCategories}
                onClick={() => {
                  DigitalData.pushNavigationInteraction(
                    NavigationEventType.MainMenu,
                    child.categoryEnglishDisplayName,
                    'link_click',
                    { selectionType: getCategoryType(child.categoryId) }
                  )
                }}
              />

              {key === mainCategories.length - 1 && (
                <li className="u-px-16 u-py-16" key={'all_categories'}>
                  <a
                    data-ga-title="show-all-categories"
                    href={stripTrailingSlash(translate('AllCategories_URL'))}>
                    {translate('Show_all_categories')}
                  </a>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </div>

      <div
        className={`c-header__category-content-wrapper ${hasArticlesEntrances ? 'col-7' : 'col-9'} u-pt-24 u-pl-32 u-pr-8`}>
        {showSkeleton && <SkeletonSubCategoryColumns />}

        {allCategories?.map(
          (item: any, key: number) =>
            isCurrentId(item.categoryId) && (
              <CategorySubCategoryColumns
                key={item.categoryId + key}
                data={data}
                categoryItem={item}
                focusOnSubCategories={focusOnSubCategories}
                isCurrentId={isCurrentId}
                narrow={hasArticlesEntrances}
              />
            )
        )}
      </div>
      {hasArticlesEntrances ? (
        <div className="c-header__category-articles-wrapper col-2 u-py-24 u-pl-24">
          <ArticlesListWithData articleIds={articleIds} />
        </div>
      ) : null}
    </div>
  )
}

const CategoryThirdLevel = ({
  grandchild,
  accordionsRefreshKey,
}: {
  grandchild: any
  accordionsRefreshKey: number
}) => {
  const { translate } = useTranslations()
  const [extOpen, setExtOpen] = useState<boolean>()
  useEffect(() => {
    if (accordionsRefreshKey) {
      setExtOpen(false)
    }
  }, [accordionsRefreshKey])
  return (
    <Accordion
      extState={true}
      extOpen={extOpen}
      setExtOpen={setExtOpen}
      title={
        <span
          data-test-id="category-lvl-2"
          className="u-overflow-ellipsis"
          style={{ maxWidth: '80%' }}>
          {grandchild.categoryDisplayName}
        </span>
      }>
      <>
        <ul className="ul-stripped">
          <li>
            <a
              className="a-stripped u-fw-bold"
              key="all_categories_3rd"
              href={stripTrailingSlash(grandchild.url ?? '')}
              onClick={() =>
                DigitalData.pushNavigationInteraction(
                  NavigationEventType.MainMenu,
                  grandchild.categoryEnglishDisplayName,
                  'link_click',
                  { selectionType: getCategoryType(grandchild.categoryId) }
                )
              }>
              {translate('Header_ShowAllCategory', {
                category: grandchild.categoryDisplayName?.toLowerCase(),
              })}
            </a>
          </li>
          {grandchild.children?.map((grandgrandchild: any, key: number) => (
            <li key={grandgrandchild.categoryDisplayName + key}>
              <a
                className="a-stripped"
                data-test-id="category-lvl-3"
                href={stripTrailingSlash(grandgrandchild.url ?? '')}
                onClick={() =>
                  DigitalData.pushNavigationInteraction(
                    NavigationEventType.MainMenu,
                    grandgrandchild.categoryEnglishDisplayName,
                    'link_click',
                    { selectionType: getCategoryType(grandchild.categoryId) }
                  )
                }>
                {grandgrandchild.categoryDisplayName}
              </a>
            </li>
          ))}
        </ul>
      </>
    </Accordion>
  )
}

export const CategorySecondLevel = ({
  allCategories,
  activeCategory,
  accordionsRefreshKey,
}: {
  allCategories: any
  activeCategory: string
  accordionsRefreshKey: number
}) => {
  const { translate } = useTranslations()
  return (
    <>
      {/* Second-level categories */}
      {allCategories &&
        allCategories?.map((child: any, key: number) => (
          <ul
            key={child.categoryId + key}
            className={classNames('c-mobile-header-menu__primary-list ul-stripped ', {
              'c-mobile-header-menu__primary-list--upcoming': activeCategory !== child.categoryId,
            })}>
            <li className="u-fw-bold">
              <a
                href={stripTrailingSlash(child.url ?? '')}
                key="all_categories_2nd"
                className="c-mobile-header-menu__primary-action"
                style={{ height: 'auto', marginTop: '5px' }}
                onClick={() => {
                  DigitalData.pushNavigationInteraction(
                    NavigationEventType.MainMenu,
                    child.categoryEnglishDisplayName,
                    'link_click',
                    { selectionType: getCategoryType(child.categoryId) }
                  )
                }}>
                {translate('Header_ShowAllCategory', {
                  category: child.categoryDisplayName?.toLowerCase(),
                })}
              </a>
            </li>
            {child.children?.map((grandchild: any, key: number) => (
              <li key={grandchild.categoryDisplayName + key}>
                {grandchild.children?.length ? (
                  <CategoryThirdLevel
                    grandchild={grandchild}
                    accordionsRefreshKey={accordionsRefreshKey}
                  />
                ) : (
                  <a
                    onClick={() => {
                      DigitalData.pushNavigationInteraction(
                        NavigationEventType.MainMenu,
                        grandchild.categoryEnglishDisplayName,
                        'link_click',
                        { selectionType: getCategoryType(child.categoryId) }
                      )
                    }}
                    className="c-mobile-header-menu__primary-action"
                    href={stripTrailingSlash(grandchild.url ?? '')}>
                    {grandchild.categoryDisplayName}
                  </a>
                )}
              </li>
            ))}
          </ul>
        ))}
    </>
  )
}

export const CategoryListButton = ({
  icon,
  gaTitle,
  onClick,
  text,
  dataTestIdOverride,
}: CommonLinkProps & CategoryListButtonProps) => (
  <li className="d-flex align-items-center">
    {getMainCategoryIcon(icon, 'i-16')}
    <button
      data-ga-title={gaTitle}
      data-test-id={dataTestIdOverride || 'category-lvl-1'}
      className="c-mobile-header-menu__primary-action c-mobile-header-menu__primary-action--w-sub-menu u-pl-16 u-mb-0"
      onClick={onClick}>
      {text}
    </button>
  </li>
)

const CategoryListLink = ({
  onActive,
  setFocusOnSubCategories,
  linkHref,
  text,
  icon,
  gaTitle,
  isCurrent,
  subCategories = true,
  onClick,
}: CommonLinkProps & CategoryListLink) => {
  const { translate } = useTranslations()

  const handleEnterPress = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key !== 'Enter' || !subCategories) {
      return
    }

    event.preventDefault()
    onActive()

    if (setFocusOnSubCategories) {
      setFocusOnSubCategories(true)
      setTimeout(() => setFocusOnSubCategories(false), 200)
    }
  }

  return (
    <li onMouseEnter={onActive} className="d-flex align-items-center">
      <a
        onClick={onClick}
        className={classNames('c-header__lvl1-category-link a-stripped', {
          'c-header__lvl1-category-link--active': isCurrent,
        })}
        data-ga-title={gaTitle}
        href={linkHref}>
        <span className="d-flex align-items-center">
          {getMainCategoryIcon(icon, 'i-20 u-mr-8 flex-shrink-0')}
          <span className="u-fs-16">{text}</span>
        </span>
      </a>
      {subCategories && (
        <a
          className="c-header__lvl1-category-link-subcategories"
          aria-label={translate('Header_Sub_Category_Accessibility')}
          onKeyDown={handleEnterPress}
          href="#">
          <Icon name="arrow-right" className="i-20" />
        </a>
      )}
    </li>
  )
}

const CategorySubCategoryColumns = ({
  data,
  categoryItem,
  isCurrentId,
  narrow,
  focusOnSubCategories,
}: CategorySubCategoryColumns) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (focusOnSubCategories) {
      containerRef.current?.focus()
    }
  }, [focusOnSubCategories])

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={classNames('row sm-gutters', {
        'd-none': !isCurrentId(categoryItem.categoryId),
      })}
      role="tabpanel"
      id={`tab-pane-${categoryItem.categoryId}`}
      key={categoryItem.categoryId}
      aria-labelledby={'TODO: categoryItemparentCurrentId (?)'}>
      {data &&
        categoryItem.children?.map((child: any, key: number) => (
          <div
            key={child.categoryDisplayName + key}
            className={`col-4 ${narrow ? ' col-xl-4' : 'col-xl-3'} col-xl-3 u-mb-16`}>
            <a
              href={stripTrailingSlash(child.url ?? '')}
              className="c-header__lvl2-category-link a-stripped h4"
              onClick={() => {
                DigitalData.pushNavigationInteraction(
                  NavigationEventType.MainMenu,
                  child.categoryEnglishDisplayName,
                  'link_click',
                  { selectionType: getCategoryType(child.categoryId) }
                )
              }}>
              {child.categoryDisplayName}
            </a>
            <ul className="ul-stripped u-fs-14">
              {child.children?.map((grandChild: any, key: number) => (
                <li key={grandChild.categoryDisplayName + key}>
                  <a
                    data-test-id="category-lvl-3"
                    className="c-header__lvl3-category-link a-stripped"
                    href={stripTrailingSlash(grandChild.url ?? '')}
                    onClick={() => {
                      DigitalData.pushNavigationInteraction(
                        NavigationEventType.MainMenu,
                        grandChild.categoryEnglishDisplayName,
                        'link_click',
                        { selectionType: getCategoryType(child.categoryId) }
                      )
                    }}>
                    {grandChild.categoryDisplayName}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  )
}

const SkeletonSubCategoryColumns = () => (
  <div className="row sm-gutters">
    {[...Array(9)].map((_, i) => (
      <div className="col-4 col-xl-3 u-mb-16" key={i}>
        <TextSkeleton type="h3" shade="light-on-dark-bg" />
        <TextSkeleton type="p" shade="light-on-dark-bg" />
      </div>
    ))}
  </div>
)

export default CategoryTreeMenu
